import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Check, CheckCircle2 } from "lucide-react";

import { api } from "../utils/apiClient";
import { getUserFromToken } from "../utils/jwtUtils";
import LoadingSpinner from "./LoadingSpinner";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/Dialog";

const formatPrice = (cents) => {
  if (cents === null || cents === undefined) return null;
  const dollars = cents / 100;
  const formatted = Number.isInteger(dollars)
    ? dollars.toLocaleString("en-US")
    : dollars.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `$${formatted}`;
};

const formatDate = (iso) => {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
};

const addonMetaLabel = (addon) =>
  addon.cadence === "monthly"
    ? `Recurring · ${formatPrice(addon.price_cents)}/mo`
    : `One-time · ${formatPrice(addon.price_cents)}`;

const addonPriceLabel = (addon) =>
  `${formatPrice(addon.price_cents)}${addon.cadence === "monthly" ? "/mo" : ""} · ${addon.price_note}`;

const ADDON_PURCHASED_STATUSES = new Set(["paid", "active"]);

// Extra property is the only add-on sold by the unit — everything else is bought one at a
// time, and buying it again (even once already purchased) just starts another purchase.
const MULTI_QUANTITY_ADDON_KEY = "extra_property";
const MAX_ADDON_QUANTITY = 20;

const getAddonButtonLabel = (isPending, isBuying) => {
  if (isPending) return "Processing...";
  if (isBuying) return "Redirecting...";
  return "Buy Now";
};

const getAddonRepurchaseLabel = (isBuying, isMultiQuantity) => {
  if (!isBuying) return "Buy again";
  return isMultiQuantity ? "Updating..." : "Redirecting...";
};

const Pricing = () => {
  const navigate = useNavigate();
  const [billingCycle, setBillingCycle] = useState("monthly");
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [user, setUser] = useState(() => getUserFromToken(localStorage.getItem("accessToken")));

  const [addonStatuses, setAddonStatuses] = useState({});
  const [addonQuantities, setAddonQuantities] = useState({});
  const [buyingAddonKey, setBuyingAddonKey] = useState(null);
  const [addonError, setAddonError] = useState(null);
  const [confirmingAddon, setConfirmingAddon] = useState(null);
  // Drives the extra_property confirm modal through confirm -> processing -> success | error.
  // The backend charges the card on file synchronously (proration_behavior=always_invoice,
  // payment_behavior=error_if_incomplete), so we hold on "processing" until that POST resolves
  // and only report success once the charge is actually confirmed.
  const [purchasePhase, setPurchasePhase] = useState("confirm");
  const [purchaseError, setPurchaseError] = useState(null);
  const [purchaseResult, setPurchaseResult] = useState(null);
  const [portalLoading, setPortalLoading] = useState(false);

  useEffect(() => {
    const handleAuthChange = () => {
      setUser(getUserFromToken(localStorage.getItem("accessToken")));
    };
    window.addEventListener("authChange", handleAuthChange);
    return () => window.removeEventListener("authChange", handleAuthChange);
  }, []);

  useEffect(() => {
    let active = true;
    api
      .get("/pricing", true)
      .then((res) => {
        if (active) setPricing(res.data);
      })
      .catch(() => {
        if (active) setError("We couldn't load pricing right now. Please try again shortly.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const refreshAddonStatuses = async () => {
    const res = await api.get("/payment/addon-status");
    const map = {};
    (res.data?.addons || []).forEach((purchase) => {
      map[purchase.addon_key] = purchase;
    });
    setAddonStatuses(map);
    return map;
  };

  // Only logged-in users can have add-on purchases; re-fetch whenever auth state changes.
  useEffect(() => {
    if (!user) {
      setAddonStatuses({});
      return;
    }
    let active = true;
    api
      .get("/payment/addon-status")
      .then((res) => {
        if (!active) return;
        const map = {};
        (res.data?.addons || []).forEach((purchase) => {
          map[purchase.addon_key] = purchase;
        });
        setAddonStatuses(map);
      })
      .catch(() => {
        // Non-blocking — add-on cards just fall back to the default buyable state.
      });
    return () => {
      active = false;
    };
  }, [user]);

  const getAddonQuantity = (addonKey) => addonQuantities[addonKey] ?? 1;

  const adjustAddonQuantity = (addonKey, delta) => {
    setAddonQuantities((prev) => {
      const current = prev[addonKey] ?? 1;
      const next = Math.min(MAX_ADDON_QUANTITY, Math.max(1, current + delta));
      return { ...prev, [addonKey]: next };
    });
  };

  // Starts a fresh Checkout redirect: a first purchase, or an independent repurchase of any
  // add-on other than extra_property (each one always creates its own new checkout/subscription).
  const handleBuyAddon = async (addon) => {
    const addonKey = addon.key;
    if (!addonKey) return;

    if (!user) {
      navigate(`/pricing?auth=signup&next=${encodeURIComponent("/pricing")}`);
      return;
    }

    const quantity = addonKey === MULTI_QUANTITY_ADDON_KEY ? getAddonQuantity(addonKey) : 1;

    setAddonError(null);
    setBuyingAddonKey(addonKey);
    try {
      const response = await api.post("/payment/create-addon-order", {
        addon_key: addonKey,
        quantity,
      });
      const { checkout_url } = response.data;

      if (checkout_url) {
        window.location.href = checkout_url;
        return;
      }
      if (addonKey !== MULTI_QUANTITY_ADDON_KEY) {
        throw new Error("No checkout URL received from payment provider");
      }
      // extra_property already had an active subscription server-side by the time this
      // request landed (e.g. a prior checkout completed via webhook after this page's addon
      // status was last fetched) — the backend charged the quantity bump immediately instead
      // of starting a new checkout, same as the buy-more flow below.
      await refreshAddonStatuses();
      setBuyingAddonKey(null);
    } catch (err) {
      setBuyingAddonKey(null);
      const detail = err?.response?.data?.detail || err.message || "Something went wrong";
      setAddonError({ key: addonKey, message: detail });
    }
  };

  const closePurchaseModal = () => {
    // Guard: never let the modal be dismissed mid-charge, so the user can't wander off
    // while their card is being charged and miss the outcome.
    if (purchasePhase === "processing" || portalLoading) return;
    setConfirmingAddon(null);
    setPurchasePhase("confirm");
    setPurchaseError(null);
    setPurchaseResult(null);
  };

  // Opens Stripe's hosted Billing Portal so the user can fix a declined card, then comes back to
  // /pricing (set as the return_url server-side) to retry. Redirects on success.
  const handleOpenBillingPortal = async () => {
    setPortalLoading(true);
    try {
      const res = await api.post("/payment/portal");
      if (res.data?.url) {
        window.location.href = res.data.url;
        return;
      }
      setPurchaseError("Could not open the billing portal. Please try again.");
    } catch (err) {
      const detail =
        err?.response?.data?.detail || err.message || "Could not open the billing portal.";
      setPurchaseError(detail);
    } finally {
      setPortalLoading(false);
    }
  };

  // Buying more of an already-active extra_property subscription charges the card on file for
  // a pro-rata quantity bump — the backend does this synchronously and only returns once Stripe
  // has both applied the bump and collected payment (or raised on a declined card). So we drive
  // the modal straight off this single POST: null checkout_url + 200 => charged & confirmed;
  // a 402 => declined, nothing granted. (A checkout_url only comes back in the rare fall-through
  // case where there's no active subscription item to bump, e.g. a brand-new purchase.)
  const handleConfirmBuyMoreExtraProperty = async () => {
    if (!confirmingAddon) return;
    const { addon } = confirmingAddon;
    const addonKey = addon.key;
    if (!addonKey || !user) return;

    const quantity = getAddonQuantity(addonKey);
    setAddonError(null);
    setPurchaseError(null);
    setPurchasePhase("processing");
    setBuyingAddonKey(addonKey);
    try {
      const response = await api.post("/payment/create-addon-order", {
        addon_key: addonKey,
        quantity,
      });
      const { checkout_url } = response.data;

      if (checkout_url) {
        window.location.href = checkout_url;
        return;
      }

      const map = await refreshAddonStatuses();
      setPurchaseResult({
        name: addon.name,
        newQuantity: response.data.quantity,
        newMonthlyCents: response.data.amount,
        renewalDate: map?.[addonKey]?.current_period_end ?? null,
      });
      setPurchasePhase("success");
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message || "Something went wrong";
      setPurchaseError(detail);
      setPurchasePhase("error");
    } finally {
      setBuyingAddonKey(null);
    }
  };

  // Entry point for both the "Buy Now" and "Buy again" buttons on the extra_property card.
  // `addonStatuses` can be stale (e.g. a previous checkout completed via webhook after this
  // page last fetched addon status), and the backend decides independently of what the button
  // says whether a purchase attempt bumps an existing active subscription (charging instantly)
  // or starts a fresh checkout. Re-checking status right before acting keeps the confirmation
  // modal in front of any instant charge instead of letting a stale button skip it.
  const startExtraPropertyPurchase = async (addon) => {
    const addonKey = addon.key;
    if (!addonKey) return;

    if (!user) {
      navigate(`/pricing?auth=signup&next=${encodeURIComponent("/pricing")}`);
      return;
    }

    setAddonError(null);
    setBuyingAddonKey(addonKey);

    let currentStatus = addonStatuses[addonKey];
    try {
      const res = await api.get("/payment/addon-status");
      const map = {};
      (res.data?.addons || []).forEach((purchase) => {
        map[purchase.addon_key] = purchase;
      });
      setAddonStatuses(map);
      currentStatus = map[addonKey];
    } catch {
      // Non-blocking — fall back to the last-known status if the refresh itself fails.
    }

    if (currentStatus && ADDON_PURCHASED_STATUSES.has(currentStatus.status)) {
      setBuyingAddonKey(null);
      setPurchasePhase("confirm");
      setPurchaseError(null);
      setPurchaseResult(null);
      setConfirmingAddon({
        addon,
        quantity: getAddonQuantity(addonKey),
        currentQuantity: currentStatus.quantity,
      });
    } else {
      await handleBuyAddon(addon);
    }
  };

  const isCurrentPlan = (planId) =>
    user?.subscriptionPlan && user.subscriptionPlan !== "free" && user.subscriptionPlan === planId;

  const handleCta = (tier) => {
    const { type } = tier.cta;
    const planId = tier.id;

    if (type === "contact") {
      navigate("/?open_inquiry=1#contact");
      return;
    }
    if (type === "signup") {
      navigate("/pricing?auth=signup");
      return;
    }
    // type === "buy"
    const target = `/checkout?plan=${planId}&cycle=${billingCycle}`;
    if (!user) {
      navigate(`/pricing?auth=signup&selected_plan=${planId}&next=${encodeURIComponent(target)}`);
      return;
    }
    if (isCurrentPlan(planId)) return;
    navigate(target);
  };

  const priceForTier = (tier) => {
    if (tier.price.custom) return { amount: "Custom", suffix: "", note: "" };
    if (tier.price.monthly_cents === 0) return { amount: "Free", suffix: "", note: "" };
    const cents =
      billingCycle === "annual" ? tier.price.annual_monthly_cents : tier.price.monthly_cents;
    const amount = formatPrice(cents);
    return { amount, suffix: "/mo", note: billingCycle === "annual" ? "billed annually" : "" };
  };

  const ctaLabel = (tier) => {
    if (isCurrentPlan(tier.id)) return "Current plan";
    return tier.cta.label;
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (error || !pricing) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-8">
        <p className="text-on-surface-variant text-xl text-center">{error}</p>
      </div>
    );
  }

  const { tiers, comparison, addons } = pricing;
  const colCount = tiers.length;

  const confirmAddon = confirmingAddon?.addon;
  const confirmAddedQty = confirmingAddon?.quantity ?? 0;
  const confirmCurrentQty = confirmingAddon?.currentQuantity ?? 0;
  const confirmNewTotalQty = confirmCurrentQty + confirmAddedQty;
  const confirmNewMonthlyCents = (confirmAddon?.price_cents ?? 0) * confirmNewTotalQty;

  return (
    <section id="pricing-section" className="pt-12 pb-24 px-8 max-w-8xl mx-auto">
      {/* Header */}
      <div className="text-center mb-12 space-y-4">
        <h1 className="text-4xl lg:text-6xl font-headline font-extrabold tracking-tight text-on-surface">
          Pick the stage you're ready for.
        </h1>
        <p className="text-on-surface-variant text-lg lg:text-xl max-w-3xl mx-auto">
          Every plan includes the full audit engine. The tiers differ in how often you can run it,
          how many engines you can test, and how much we automate.
        </p>
      </div>

      {/* Billing toggle */}
      <div className="flex justify-center mb-14">
        <div className="inline-flex items-center bg-surface-container-high rounded-full p-1">
          {[
            { key: "monthly", label: "Monthly" },
            { key: "annual", label: "Annual" },
          ].map((option) => (
            <button
              key={option.key}
              type="button"
              onClick={() => setBillingCycle(option.key)}
              className={`px-6 py-2 rounded-full text-sm font-bold transition-all ${
                billingCycle === option.key
                  ? "bg-surface-container-lowest text-primary shadow-sm"
                  : "text-on-surface-variant hover:text-on-surface"
              }`}
            >
              {option.label}
              {option.key === "annual" && (
                <span className="ml-2 text-xs text-tertiary font-semibold">Save up to 17%</span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Tier cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-6 mb-24">
        {tiers.map((tier) => {
          const price = priceForTier(tier);
          const savings =
            billingCycle === "annual" && tier.price.annual_savings_year_cents
              ? formatPrice(tier.price.annual_savings_year_cents)
              : null;
          return (
            <div
              key={tier.id}
              className={`relative flex flex-col rounded-3xl p-7 bg-surface-container-lowest transition-all ${
                tier.highlight
                  ? "ring-2 ring-primary shadow-2xl xl:scale-[1.03]"
                  : "shadow-cloud ring-1 ring-outline-variant/10"
              }`}
            >
              {tier.highlight && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-primary to-primary-container text-on-primary px-4 py-1 rounded-full text-[10px] font-bold tracking-widest uppercase whitespace-nowrap">
                  Most Popular
                </div>
              )}
              <div className="font-label text-[10px] font-bold tracking-widest uppercase text-on-surface-variant/60 mb-2">
                {tier.stage}
              </div>
              <h3 className="font-headline text-xl font-bold text-on-surface mb-1">{tier.name}</h3>
              <p className="text-on-surface-variant text-sm mb-5 min-h-[40px]">{tier.tagline}</p>

              <div className="mb-1">
                <span className="font-headline text-4xl font-extrabold text-on-surface">
                  {price.amount}
                </span>
                {price.suffix && (
                  <span className="text-on-surface-variant text-sm ml-1">{price.suffix}</span>
                )}
              </div>
              <div className="text-on-surface-variant text-xs min-h-[16px]">{price.note}</div>
              <div className="text-xs text-tertiary font-semibold mb-5 min-h-[16px]">
                {savings ? `Save ${savings}/yr` : ""}
              </div>

              <button
                type="button"
                onClick={() => handleCta(tier)}
                disabled={isCurrentPlan(tier.id)}
                className={`w-full py-3 rounded-xl font-bold text-sm transition-all mb-6 ${
                  tier.highlight
                    ? "bg-gradient-to-br from-primary to-primary-container text-on-primary hover:opacity-90"
                    : "bg-surface-container-high text-on-surface hover:bg-surface-container-highest"
                } disabled:opacity-60 disabled:cursor-default`}
              >
                {ctaLabel(tier)}
              </button>

              <ul className="space-y-3">
                {tier.summary_features.map((feature) => (
                  <li
                    key={feature}
                    className="flex items-start gap-2 text-sm text-on-surface-variant"
                  >
                    <Check className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>

      {/* Combined comparison */}
      <div className="text-center mb-10">
        <h2 className="font-headline text-3xl lg:text-4xl font-extrabold text-on-surface">
          Compare every plan, side by side.
        </h2>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full min-w-[860px] border-separate border-spacing-0">
          <thead>
            <tr>
              <th className="text-left p-4 align-bottom w-[28%]">
                <span className="font-label text-xs font-bold tracking-widest uppercase text-on-surface-variant/60">
                  Features
                </span>
              </th>
              {tiers.map((tier) => (
                <th
                  key={tier.id}
                  className={`p-4 align-bottom text-center ${
                    tier.highlight ? "bg-primary/5 rounded-t-2xl" : ""
                  }`}
                >
                  <div className="font-headline font-bold text-on-surface">{tier.name}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {comparison.groups.map((group) => (
              <GroupRows key={group.name} group={group} tiers={tiers} colCount={colCount} />
            ))}
          </tbody>
        </table>
      </div>

      {/* Add-ons */}
      {addons?.length > 0 && (
        <div className="mt-24">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
            <div>
              <div className="font-label text-[10px] font-bold tracking-widest uppercase text-tertiary mb-2">
                Add-ons
              </div>
              <h2 className="font-headline text-3xl lg:text-4xl font-extrabold text-on-surface">
                Stack on what you actually need.
              </h2>
            </div>
            <p className="font-label text-sm text-on-surface-variant max-w-xs md:text-right">
              Every add-on is optional and works on any plan from Visible up.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {addons.map((addon) => {
              const addonKey = addon.key;
              const status = addonKey ? addonStatuses[addonKey] : null;
              const isPurchased = status && ADDON_PURCHASED_STATUSES.has(status.status);
              const isPending = status?.status === "pending";
              const isBuying = buyingAddonKey === addonKey;
              const disabled = !addonKey || isBuying || isPending;
              const isMultiQuantity = addonKey === MULTI_QUANTITY_ADDON_KEY;
              const quantity = getAddonQuantity(addonKey);

              const buttonLabel = getAddonButtonLabel(isPending, isBuying);

              return (
                <div
                  key={addon.key || addon.name}
                  className="flex flex-col rounded-3xl p-7 bg-surface-container-lowest shadow-cloud ring-1 ring-outline-variant/10"
                >
                  <div className="font-label text-[10px] font-bold tracking-widest uppercase text-on-surface-variant/60 mb-4">
                    {addonMetaLabel(addon)}
                  </div>
                  <h3 className="font-headline text-xl font-bold text-on-surface mb-3">
                    {addon.name}
                  </h3>
                  <p className="text-sm text-on-surface-variant mb-6 flex-1">{addon.description}</p>
                  <div className="font-label text-sm font-bold text-primary mb-5">
                    {addonPriceLabel(addon)}
                  </div>

                  {isMultiQuantity && (
                    <div className="flex items-center gap-2 mb-4">
                      <span className="text-xs font-semibold text-on-surface-variant">
                        Quantity
                      </span>
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() => adjustAddonQuantity(addonKey, -1)}
                          disabled={isBuying || isPending || quantity <= 1}
                          aria-label="Decrease quantity"
                          className="w-7 h-7 rounded-lg bg-surface-container-high hover:bg-surface-container-highest text-sm font-bold disabled:opacity-40 disabled:cursor-default"
                        >
                          −
                        </button>
                        <span className="w-6 text-center text-sm font-semibold text-on-surface">
                          {quantity}
                        </span>
                        <button
                          type="button"
                          onClick={() => adjustAddonQuantity(addonKey, 1)}
                          disabled={isBuying || isPending || quantity >= MAX_ADDON_QUANTITY}
                          aria-label="Increase quantity"
                          className="w-7 h-7 rounded-lg bg-surface-container-high hover:bg-surface-container-highest text-sm font-bold disabled:opacity-40 disabled:cursor-default"
                        >
                          +
                        </button>
                      </div>
                    </div>
                  )}

                  {isPurchased ? (
                    <div className="flex items-center justify-between gap-3">
                      <span className="font-label text-xs font-bold tracking-wide uppercase text-tertiary">
                        {isMultiQuantity ? `Owns ${status.quantity}` : "Purchased"}
                      </span>
                      <button
                        type="button"
                        onClick={() =>
                          isMultiQuantity
                            ? startExtraPropertyPurchase(addon)
                            : handleBuyAddon(addon)
                        }
                        disabled={isBuying}
                        className="px-3 py-1.5 rounded-lg text-xs font-semibold text-on-surface-variant bg-surface-container-high hover:bg-surface-container-highest disabled:opacity-60 disabled:cursor-default"
                      >
                        {getAddonRepurchaseLabel(isBuying, isMultiQuantity)}
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() =>
                        isMultiQuantity ? startExtraPropertyPurchase(addon) : handleBuyAddon(addon)
                      }
                      disabled={disabled}
                      className="w-full py-3 rounded-xl font-bold text-sm transition-all bg-surface-container-high text-on-surface hover:bg-surface-container-highest disabled:opacity-60 disabled:cursor-default"
                    >
                      {buttonLabel}
                    </button>
                  )}

                  {addonError?.key === addonKey && (
                    <p className="mt-2 text-xs text-error">{addonError.message}</p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <Dialog
        open={!!confirmingAddon}
        onOpenChange={(open) => {
          if (!open) closePurchaseModal();
        }}
      >
        <DialogContent hideClose={purchasePhase === "processing"}>
          {purchasePhase === "confirm" && (
            <>
              <DialogHeader>
                <DialogTitle>
                  Add {confirmAddedQty} more {confirmAddon?.name}?
                </DialogTitle>
                <DialogDescription>
                  You currently have {confirmCurrentQty}. Confirming charges your card on file now —
                  prorated for the days left in this billing period — and raises your subscription
                  to {formatPrice(confirmNewMonthlyCents)}/mo ({confirmNewTotalQty} total) going
                  forward.
                </DialogDescription>
              </DialogHeader>
              <DialogBody>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={closePurchaseModal}
                    className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-on-surface-variant hover:bg-surface-container-high transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleConfirmBuyMoreExtraProperty}
                    className="flex-1 bg-gradient-to-br from-primary to-primary-container text-on-primary py-2.5 rounded-xl font-bold hover:opacity-90 transition-opacity"
                  >
                    Confirm &amp; pay
                  </button>
                </div>
              </DialogBody>
            </>
          )}

          {purchasePhase === "processing" && (
            <>
              <DialogHeader>
                <DialogTitle>Charging your card…</DialogTitle>
                <DialogDescription>
                  We&apos;re charging your card on file for the additional {confirmAddon?.name}.
                  Please don&apos;t close this window.
                </DialogDescription>
              </DialogHeader>
              <DialogBody>
                <LoadingSpinner size="md" message="Processing your payment…" />
              </DialogBody>
            </>
          )}

          {purchasePhase === "success" && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <CheckCircle2 className="w-6 h-6 text-tertiary shrink-0" /> Payment successful
                </DialogTitle>
                <DialogDescription>
                  Your card was charged and you now have {purchaseResult?.newQuantity}{" "}
                  {purchaseResult?.name}. Your subscription is now{" "}
                  {formatPrice(purchaseResult?.newMonthlyCents)}/mo
                  {purchaseResult?.renewalDate && formatDate(purchaseResult.renewalDate)
                    ? `, renewing ${formatDate(purchaseResult.renewalDate)}`
                    : ""}
                  .
                </DialogDescription>
              </DialogHeader>
              <DialogBody>
                <button
                  type="button"
                  onClick={closePurchaseModal}
                  className="w-full bg-gradient-to-br from-primary to-primary-container text-on-primary py-2.5 rounded-xl font-bold hover:opacity-90 transition-opacity"
                >
                  Done
                </button>
              </DialogBody>
            </>
          )}

          {purchasePhase === "error" && (
            <>
              <DialogHeader>
                <DialogTitle>Payment failed</DialogTitle>
                <DialogDescription>
                  {purchaseError} If your card was declined, update your payment method and try
                  again.
                </DialogDescription>
              </DialogHeader>
              <DialogBody>
                <div className="space-y-3">
                  <button
                    type="button"
                    onClick={handleOpenBillingPortal}
                    disabled={portalLoading}
                    className="w-full bg-gradient-to-br from-primary to-primary-container text-on-primary py-2.5 rounded-xl font-bold hover:opacity-90 transition-opacity disabled:opacity-60 disabled:cursor-default"
                  >
                    {portalLoading ? "Opening…" : "Update payment method"}
                  </button>
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={closePurchaseModal}
                      disabled={portalLoading}
                      className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-on-surface-variant hover:bg-surface-container-high transition-colors disabled:opacity-60 disabled:cursor-default"
                    >
                      Close
                    </button>
                    <button
                      type="button"
                      onClick={handleConfirmBuyMoreExtraProperty}
                      disabled={portalLoading}
                      className="flex-1 bg-surface-container-high text-on-surface py-2.5 rounded-xl font-bold hover:bg-surface-container-highest transition-colors disabled:opacity-60 disabled:cursor-default"
                    >
                      Try again
                    </button>
                  </div>
                </div>
              </DialogBody>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};

const GroupRows = ({ group, tiers, colCount }) => (
  <>
    <tr>
      <td
        colSpan={colCount + 1}
        className="pt-8 pb-2 px-4 font-headline text-sm font-bold uppercase tracking-wider text-primary"
      >
        {group.name}
      </td>
    </tr>
    {group.rows.map((row) => (
      <tr key={row.label}>
        <td className="p-4 text-sm font-medium text-on-surface border-t border-surface-container-high">
          {row.label}
        </td>
        {tiers.map((tier) => {
          const value = row.values[tier.id] ?? "—";
          const isEmpty = value === "—";
          return (
            <td
              key={tier.id}
              className={`p-4 text-sm text-center border-t border-surface-container-high ${
                tier.highlight ? "bg-primary/5" : ""
              } ${isEmpty ? "text-on-surface-variant/40" : "text-on-surface"}`}
            >
              {value}
            </td>
          );
        })}
      </tr>
    ))}
  </>
);

export default Pricing;
